import { memo, useCallback } from "react";
import { Link } from "@tanstack/react-router";

import { cn } from "../../lib/utils";
import { HeyDexWordmark } from "../HeyDexWordmark";
import { SidebarFooter, SidebarTrigger } from "../ui/sidebar";
import { SidebarAllowance } from "./SidebarAllowance";
import { SidebarThreadUndoNotice } from "./SidebarThreadUndoNotice";
import { observeResize } from "~/lib/observeResize";
import { insideHeyDayNewsSite } from "~/appMode";

/**
 * The sidebar's titlebar row: its toggle in the phone's drawer, and the lockup where HeyDex
 * is not already under heyday.news's masthead.
 */
export const SidebarChromeHeader = memo(function SidebarChromeHeader() {
  return (
    // The titlebar row, not a padded SidebarHeader: it aligns to the window controls.
    <div
      className={cn(
        "relative flex h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center gap-2 px-3 md:pl-0",
        // On the desktop site the sidebar's toggle shares the New thread row, so no row holds it alone.
        insideHeyDayNewsSite && "md:hidden",
      )}
    >
      <SidebarTrigger variant="ghost" className="relative top-auto z-10 translate-y-0 md:hidden" />
      {/* The padding keeps the brand's focus ring inside the clip. */}
      <div
        className={cn(
          "relative z-10 flex h-8 min-w-0 flex-1 flex-wrap content-start items-center gap-x-2 overflow-hidden py-0.5",
          insideHeyDayNewsSite && "hidden",
        )}
      >
        <SidebarBrand />
      </div>
    </div>
  );
});

// Measures the brand at its titlebar inset, plus the header's right padding and the
// sidebar border, so the sidebar minimum follows font size, zoom and macOS window controls.
export function SidebarBrandWidthProbe({
  onWidthChange,
}: {
  onWidthChange: (width: number) => void;
}) {
  const observeWidth = useCallback(
    (probe: HTMLDivElement) => {
      return observeResize(probe, ([entry]) => {
        if (entry) onWidthChange(entry.borderBoxSize[0]?.inlineSize ?? probe.offsetWidth);
      });
    },
    [onWidthChange],
  );

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none invisible fixed top-0 left-0 flex w-max border-r border-transparent pr-3"
      ref={observeWidth}
    >
      <div className="ml-[var(--workspace-titlebar-content-left)] flex">
        <SidebarBrandMark />
      </div>
    </div>
  );
}

function SidebarBrand() {
  // On heyday.news HeyDex sits under the site's masthead and wears no lockup of its own.
  if (insideHeyDayNewsSite) return null;
  return (
    <Link
      aria-label="Go to threads"
      className={cn(
        // On a phone the drawer's own toggle sits beside it; wider, the floating toggle holds its inset.
        "relative z-10 flex h-7 w-fit min-w-0 shrink-0 items-center overflow-hidden rounded-md text-foreground outline-hidden ring-ring focus-visible:ring-2 focus-visible:ring-inset md:ml-[var(--workspace-titlebar-content-left)]",
      )}
      to="/"
    >
      <SidebarBrandMark />
    </Link>
  );
}

function SidebarBrandMark() {
  return <HeyDexWordmark height={22} />;
}

export const SidebarChromeFooter = memo(function SidebarChromeFooter() {
  return (
    <SidebarFooter>
      <SidebarThreadUndoNotice />
      <SidebarAllowance />
    </SidebarFooter>
  );
});
